import axios from "axios";
import store from "@/store";
import { AppConfig } from "../base/AppConfig";
import { Serializable } from "../base/IBaseEntity";
import { JsonResult } from "../base/JsonResult";
import { router } from "@/router";

export interface Success {
  (data: any): void;
}

export interface Failure {
  (error: Error): void;
}

export interface CallBack<T> {
  (data: T): void;
}

export class Sorter {
  prop = "";
  sortField = "";
  sortMode = "";
}

export class Pager {
  pageIndex = 1;
  pageSize = 100;
  total = 0;
  sorters: Sorter[] = [];
}

export class QueryParam {
  pager?: Pager = new Pager();
  entity: any;
  extendMap?: any;
}

export class DataService {
  private baseUrl = "";

  constructor() {
    this.baseUrl = AppConfig.Config.ServerUrl;
    axios.defaults.baseURL = this.baseUrl;
  }

  public get(path: string, params: any): Promise<any> {
    const promise = new Promise<any>(function (resolve, reject) {
      try {
        if (store.getters.loginState === 1) {
          axios.defaults.headers.common.Authorization =
            store.getters.loginToken;
        }

        axios
          .get(path, { params })
          .then(function (response) {
            if (response.status === 200) {
              const resultJson = response.data as JsonResult;
              if (resultJson.success) {
                resolve(resultJson);
              } else {
                if (resultJson.code === "101") {
                  router.push({ path: "/login" });
                } else {
                  reject(new Error(resultJson.code + ":" + resultJson.message));
                }
              }
            } else {
              reject(new Error(response.statusText));
            }
          })
          .catch(function (error) {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
    return promise;
  }

  public post(path: string, data: any): Promise<any> {
    const promise = new Promise<any>(function (resolve, reject) {
      try {
        if (store.getters.loginState === 1) {
          axios.defaults.headers.common.Authorization =
            store.getters.loginToken;
        }
        axios
          .post(path, data)
          .then(function (response) {
            if (response.status === 200) {
              const resultJson = response.data as JsonResult;
              if (resultJson.success) {
                resolve(resultJson);
              } else {
                if (resultJson.code === "101") {
                  router.push({ path: "/login" });
                } else {
                  reject(new Error(resultJson.code + ":" + resultJson.message));
                }
              }
            } else {
              reject(new Error(response.statusText));
            }
          })
          .catch(function (error) {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
    return promise;
  }

  public postText(path: string, data: string): Promise<any> {
    const promise = new Promise<any>(function (resolve, reject) {
      try {
        if (store.getters.loginState === 1) {
          axios.defaults.headers.common.Authorization =
            store.getters.loginToken;
        }
        axios
          .post(path, data, { headers: { "Content-Type": "text/plain" } })
          .then(function (response) {
            if (response.status === 200) {
              const resultJson = response.data as JsonResult;
              if (resultJson.success) {
                resolve(resultJson);
              } else {
                if (resultJson.code === "101") {
                  router.push({ path: "/login" });
                } else {
                  reject(new Error(resultJson.code + ":" + resultJson.message));
                }
              }
            } else {
              reject(new Error(response.statusText));
            }
          })
          .catch(function (error) {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
    return promise;
  }

  public postReturnResponse(path: string, data: any): Promise<any> {
    const promise = new Promise<any>(function (resolve, reject) {
      try {
        axios
          .post(path, data)
          .then(function (response) {
            if (response.status === 200) {
              const resultJson = response.data as JsonResult;
              if (resultJson.success) {
                resolve(response);
              } else {
                reject(new Error(resultJson.code + ":" + resultJson.message));
              }
            } else {
              reject(new Error(response.statusText));
            }
          })
          .catch(function (error) {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
    return promise;
  }

  public exportExcel(path: string, data: any, fileName: string): Promise<any> {
    const promise = new Promise<any>(function (resolve, reject) {
      try {
        if (store.getters.loginState === 1) {
          axios.defaults.headers.common.Authorization =
            store.getters.loginToken;
        }
        axios
          .post(path, data, { responseType: 'blob' })
          .then(function (response) {
            if (response.status === 200) {
              if (response.data.type === 'application/json') {
                const reader = new FileReader();
                reader.onload = function (event) {
                  const content = reader.result;
                  reject(content);
                };
                reader.readAsText(response.data);
              } else {
                const blob = new Blob([response.data], {
                  type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
                });
                const a = document.createElement("a");
                const href = window.URL.createObjectURL(blob); // 创建链接对象
                a.href = href;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(href); // 移除链接对象
                document.body.removeChild(a); // 移除a元素
                resolve(response.status);
              }
            } else {
              reject(new Error(response.statusText));
            }
          })
          .catch(function (error) {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
    return promise;
  }

  public uploadFile(data: any): Promise<any> {
    const promise = new Promise<any>(function (resolve, reject) {
      try {
        if (store.getters.loginState === 1) {
          axios.defaults.headers.common.Authorization =
            store.getters.loginToken;
        }
        axios
          .post("api/file/upload", data)
          .then(function (response) {
            if (response.status === 200) {
              const resultJson = response.data as JsonResult;
              if (resultJson.success) {
                resolve(resultJson);
              } else {
                reject(new Error(resultJson.code + ":" + resultJson.message));
              }
            } else {
              reject(new Error(response.statusText));
            }
          })
          .catch(function (error) {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
    return promise;
  }

  public importExcel(api: string, formData: FormData, callBack: CallBack<JsonResult>) {
    if (store.getters.loginState === 1) {
      axios.defaults.headers.common.Authorization =
        store.getters.loginToken;
    }

    axios
      .post(api, formData, { timeout: 60 * 60 * 1000 })
      .then(function (response) {
        if (response.status === 200) {
          const resultJson = response.data as JsonResult;
          if (resultJson.success) {
            callBack(resultJson);
          } else {
            throw new Error(resultJson.code + ":" + resultJson.message);
          }
        } else {
          throw new Error(response.statusText);
        }
      })
      .catch(function (error) {
        console.error("import excel error:", error);
        throw error;
      });
  }
}
